<script>
  import { onDestroy } from 'svelte';
  import { getApiBase } from './apiBase.js';

  export let month;
  export let teacherId = null;
  export let refreshKey = 0;

  let data = null;
  let teachers = [];
  let loading = false;
  let error = '';
  let teacher = '';
  let party = '';
  let type = '';
  let search = '';
  let query = '';
  let page = 1;
  let context = '';
  let scope = '';
  let requestVersion = 0;
  let controller;
  const types = { Velavimas: 'Late', Atsaukimas: 'Cancellation', Kitas: 'Other' };
  const dateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Vilnius', dateStyle: 'medium', timeStyle: 'short' });
  const monthFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Vilnius', month: 'long', year: 'numeric' });
  $: monthLabel = month ? monthFormat.format(new Date(`${month}-15T12:00:00Z`)) : '';
  $: hasFilters = Boolean(teacher || party || type || query);
  $: if (month && `${month}:${teacherId}:${refreshKey}` !== context) {
    context = `${month}:${teacherId}:${refreshKey}`;
    if (scope !== `${month}:${teacherId}`) {
      scope = `${month}:${teacherId}`;
      teacher = party = type = search = query = '';
      teachers = [];
      page = 1;
    }
    load();
  }

  onDestroy(() => { requestVersion += 1; controller?.abort(); });

  async function load() {
    const version = ++requestVersion;
    controller?.abort();
    controller = new AbortController();
    loading = true;
    error = '';
    data = null;
    const params = new URLSearchParams({ month, page: String(page) });
    if (teacherId) params.set('teacher_id', String(teacherId));
    if (teacher) params.set('teacher', teacher);
    if (party) params.set('party', party);
    if (type) params.set('type', type);
    if (query) params.set('q', query);
    try {
      const response = await fetch(`${getApiBase()}/studentplayground/api/class_incidents/?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken') || ''}` },
        cache: 'no-store', signal: controller.signal,
      });
      if (!response.ok) throw new Error('Could not load class incidents. Please try again.');
      const result = await response.json();
      if (version === requestVersion) { data = result; teachers = result.teachers; page = result.page; }
    } catch (cause) {
      if (version === requestVersion && cause.name !== 'AbortError') error = cause.message;
    } finally {
      if (version === requestVersion) loading = false;
    }
  }

  function filter() { page = 1; load(); }
  function clearFilters() { teacher = party = type = search = query = ''; filter(); }
  function searchIncidents(event) { event.preventDefault(); query = search.trim(); filter(); }
  function changePage(amount) { page += amount; load(); }
</script>

<section class="incident-report" aria-label="Class incidents" aria-busy={loading}>
  <header>
    <div><p class="eyebrow">Live incident register</p><h3>Class incidents</h3><p class="period">{monthLabel} · By class date · Vilnius time</p></div>
    <button type="button" class="refresh" on:click={load} disabled={loading}>Refresh incidents</button>
  </header>

  {#if loading}
    <p class="state" role="status">Loading class incidents…</p>
  {:else if error}
    <div class="state"><p role="alert">{error}</p><button type="button" on:click={load}>Try again</button></div>
  {:else if data}
    <div class="summary" aria-label="Incident totals for the selected month">
      <div><strong>{data.summary.total}</strong><span>Total incidents</span></div>
      <div><strong>{data.summary.teacher}</strong><span>Teacher responsibility</span></div>
      <div><strong>{data.summary.student}</strong><span>Student responsibility</span></div>
    </div>
    <div class="type-totals"><span><strong>{data.summary.cancellation}</strong> cancellations</span><span><strong>{data.summary.late}</strong> late arrivals</span><span><strong>{data.summary.other}</strong> other incidents</span></div>
    <p class="summary-note">Totals cover the full selected month. Filters below narrow the incident list.</p>
  {/if}

  <form class="filters" on:submit={searchIncidents}>
    {#if !teacherId}
      <label>Teacher<select bind:value={teacher} on:change={filter}><option value="">All teachers</option>{#each teachers as name}<option value={name}>{name || 'Teacher not recorded'}</option>{/each}</select></label>
    {/if}
    <label>Responsible party<select bind:value={party} on:change={filter}><option value="">Teacher and student</option><option value="Teacher">Teacher</option><option value="Student">Student</option></select></label>
    <label>Incident type<select bind:value={type} on:change={filter}><option value="">All types</option><option value="Atsaukimas">Cancellation</option><option value="Velavimas">Late</option><option value="Kitas">Other</option></select></label>
    <label class="search">Search incidents<input type="search" bind:value={search} placeholder="Class, student, teacher or comment" /></label>
    <button type="submit">Search</button>
    {#if hasFilters}<button type="button" class="clear" on:click={clearFilters}>Clear filters</button>{/if}
  </form>

  {#if data && !loading && !error}
    <div class="list-heading"><h4>Incident details</h4><p role="status">{data.count} {data.count === 1 ? 'incident' : 'incidents'}{hasFilters ? ' matching filters' : ' this month'} · Newest class first</p></div>
    {#if data.count === 0}
      <p class="state">{hasFilters ? 'No incidents match these filters.' : 'No class incidents recorded for this month.'}</p>
    {:else}
      <div class="incident-list">
        {#each data.results as incident (incident.id)}
          <details class="incident">
            <summary>
              <div class="class-info"><time datetime={incident.class_time}>{dateFormat.format(new Date(incident.class_time))}</time><strong>{incident.class_name || 'Class name not recorded'}</strong></div>
              <div class="people"><span class="person-label">Teacher</span><span>{incident.teacher_name || 'Not recorded'}</span><span class="person-label">Student{incident.student_name?.includes(',') ? 's' : ''}</span><span>{incident.student_name || 'Not recorded'}</span></div>
              <div class="classification"><span class="type" class:cancellation={incident.incident_type === 'Atsaukimas'} class:late={incident.incident_type === 'Velavimas'}>{types[incident.incident_type] || incident.incident_type}</span><span>{incident.responsible_party} responsibility</span></div>
              <span class="disclosure"><span class="closed-label">View details</span><span class="open-label">Hide details</span><span class="plus" aria-hidden="true">+</span></span>
            </summary>
            <div class="incident-details">
              <h5>Incident description</h5>
              <p class="comment">{incident.comment || 'No description provided.'}</p>
              <p class="recorded">Recorded by {incident.created_by} · {dateFormat.format(new Date(incident.created_at))}</p>
              {#if !incident.class_exists}<p class="archive-note">Original class no longer available.</p>{/if}
            </div>
          </details>
        {/each}
      </div>
      <nav class="pagination" aria-label="Incident pages">
        <span>Page {data.page} of {Math.max(1, Math.ceil(data.count / data.page_size))} · {data.count} total</span>
        <div><button type="button" aria-label="Previous page" disabled={data.page <= 1} on:click={() => changePage(-1)}>Previous</button><button type="button" aria-label="Next page" disabled={data.page * data.page_size >= data.count} on:click={() => changePage(1)}>Next</button></div>
      </nav>
    {/if}
  {/if}
</section>

<style>
  .incident-report { margin: 1.5rem 0 2rem; padding: clamp(1rem, 3vw, 1.75rem); border: 1px solid #eaeaea; border-radius: 8px; background: #fff; color: #2f3437; font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.5; min-width: 0; }
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
  .eyebrow { margin: 0 0 .3rem; text-transform: uppercase; letter-spacing: .1em; font-size: .68rem; color: #686862; font-weight: 700; }
  h3 { margin: 0; font-size: 1.5rem; letter-spacing: -.03em; font-weight: 650; }
  .period { margin: .4rem 0 0; color: #66675f; font-size: .82rem; }
  button, input, select { font: inherit; box-sizing: border-box; }
  button { border: 1px solid #d9dad5; border-radius: 5px; background: #fff; color: #343830; padding: .55rem .8rem; cursor: pointer; font-size: .8rem; white-space: nowrap; }
  button:hover:not(:disabled) { background: #f7f6f3; }
  button:disabled { opacity: .5; cursor: default; }
  :is(button, input, select, summary):focus-visible { outline: 2px solid #75643f; outline-offset: 3px; }
  .summary { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 1.5rem; background: #f7f6f3; border-radius: 6px; }
  .summary > div { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: .1rem; }
  .summary > div + div { border-left: 1px solid #e4e3de; }
  .summary strong { font-size: 1.8rem; font-weight: 600; font-variant-numeric: tabular-nums; letter-spacing: -.04em; }
  .summary span { font-size: .8rem; color: #5a5f56; }
  .type-totals { display: flex; flex-wrap: wrap; gap: .5rem 1.5rem; margin-top: .85rem; font-size: .82rem; }
  .summary-note { font-size: .75rem; color: #6b6f66; margin: .5rem 0 1.35rem; }
  .filters { display: flex; flex-wrap: wrap; gap: .75rem; align-items: flex-end; padding: 1rem 0; border-top: 1px solid #eaeaea; border-bottom: 1px solid #eaeaea; }
  label { display: flex; flex: 1 1 145px; flex-direction: column; gap: .35rem; font-size: .75rem; font-weight: 600; min-width: 0; }
  .search { flex: 2 1 220px; }
  input, select { width: 100%; border: 1px solid #d9dad5; border-radius: 5px; padding: .6rem; background: #fff; color: #343830; font-size: .8rem; min-width: 0; }
  .filters button[type=submit] { background: #30372e; color: white; border-color: #30372e; }
  .clear { border-color: transparent; text-decoration: underline; }
  .list-heading { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: .4rem 1rem; padding: 1.3rem 0 .6rem; }
  h4 { font-size: .9rem; margin: 0; font-weight: 650; }
  .list-heading p { margin: 0; font-size: .75rem; color: #666b62; }
  .incident { border-bottom: 1px solid #eaeaea; }
  summary { list-style: none; cursor: pointer; display: grid; grid-template-columns: 1.1fr 1.2fr 1fr auto; gap: 1rem; align-items: center; padding: 1rem .2rem; }
  summary::-webkit-details-marker { display: none; }
  summary:hover { background: #fbfbfa; }
  .class-info, .people, .classification { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; overflow-wrap: anywhere; }
  .class-info strong { font-size: .9rem; font-weight: 600; margin-top: .25rem; }
  time { font-size: .75rem; color: #666b62; }
  .people { font-size: .82rem; }
  .person-label { color: #6b6f66; font-size: .66rem; }
  .person-label:not(:first-child) { margin-top: .3rem; }
  .classification { gap: .4rem; font-size: .73rem; color: #61665d; }
  .type { padding: .2rem .5rem; border-radius: 4px; background: #efefec; color: #565d50; font-weight: 600; font-size: .73rem; }
  .type.cancellation { background: #fdebeb; color: #913e39; }
  .type.late { background: #fbf3db; color: #805c18; }
  .disclosure { display: flex; gap: .7rem; align-items: center; font-size: .75rem; color: #50584a; white-space: nowrap; }
  .plus { font-size: 1.25rem; }
  .open-label, details[open] .closed-label { display: none; }
  details[open] .open-label { display: inline; }
  details[open] .plus { transform: rotate(45deg); }
  .incident-details { padding: 1rem 1.2rem; margin: 0 0 1rem; background: #f9f9f8; border-left: 2px solid #d8dacf; overflow-wrap: anywhere; }
  h5 { margin: 0 0 .5rem; font-size: .75rem; color: #62675b; font-weight: 600; }
  .comment { white-space: pre-wrap; font-size: .9rem; margin: 0 0 1rem; line-height: 1.65; }
  .recorded, .archive-note { margin: .4rem 0 0; color: #666b62; font-size: .75rem; }
  .pagination { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .75rem; padding-top: 1rem; font-size: .75rem; color: #62675b; }
  .pagination div { display: flex; gap: .5rem; }
  .state { padding: 1.5rem 0; color: #62675b; font-size: .85rem; }
  @media (max-width: 640px) {
    header { flex-wrap: wrap; }
    .summary > div { padding: .8rem .6rem; }
    .summary strong { font-size: 1.5rem; }
    .summary span { font-size: .7rem; }
    summary { grid-template-columns: 1fr 1fr; gap: .8rem; }
    .disclosure { justify-self: end; }
    .incident-details { padding: 1rem .8rem; }
  }
</style>
